import type { AssignedRundownCanvas, OutputConflict, OutputGroup } from "./types";

export type { OutputConflict } from "./types";

const isAssignedToOutput = (canvas: RundownTemplateCanvas): canvas is AssignedRundownCanvas =>
	canvas.output?.id !== undefined;

const groupCanvasesByOutput = (rundownCanvases: RundownTemplateCanvas[]): OutputGroup[] => {
	const groups = new Map<string, OutputGroup>();
	for (const canvas of rundownCanvases.filter(isAssignedToOutput)) {
		const existing = groups.get(canvas.output.id);
		if (existing) {
			existing.canvases.push(canvas);
		} else {
			groups.set(canvas.output.id, { output: canvas.output, canvases: [canvas] });
		}
	}
	return [...groups.values()];
};

const hasResolutionConflict = (canvases: Canvas[]): boolean => {
	const uniqueResolutions = new Set(canvases.map(({ width, height }) => `${width}x${height}`));
	return uniqueResolutions.size > 1;
};

export const formatConflictMessage = ({ output, canvases }: OutputConflict): string => {
	const list = canvases.map((c) => `'${c.name}' (${c.width}×${c.height})`).join(", ");
	return `Output '${output.name}' has canvases with mismatched resolutions: ${list}.`;
};

export const getOutputConflicts = (
	rundownCanvases: RundownTemplateCanvas[],
	canvases: Canvas[],
): OutputConflict[] =>
	groupCanvasesByOutput(rundownCanvases).flatMap(({ output, canvases: outputCanvases }) => {
		const matchedCanvases = outputCanvases.flatMap((canvas) =>
			canvases.filter(({ id }) => id === canvas.id),
		);
		return hasResolutionConflict(matchedCanvases) ? [{ output, canvases: matchedCanvases }] : [];
	});
